import { Note, Collection, Task, TaskLink, Folder } from '@shared/types';
import { storage } from '@shared/lib/storage';

// Offline data storage for when the app is completely offline
export class OfflineDataManager {
  private static instance: OfflineDataManager;
  private notes: Note[] = [];
  private collections: Collection[] = [];
  private tasks: Task[] = [];
  private folders: Folder[] = [];

  static getInstance(): OfflineDataManager {
    if (!OfflineDataManager.instance) {
      OfflineDataManager.instance = new OfflineDataManager();
    }
    return OfflineDataManager.instance;
  }

  // Load data from localStorage
  loadFromStorage(): void {
    try {
      const storedNotes = storage.getItem('hintword_offline_notes');
      if (storedNotes) {
        this.notes = JSON.parse(storedNotes);
      }

      const storedCollections = storage.getItem('hintword_offline_collections');
      if (storedCollections) {
        this.collections = JSON.parse(storedCollections);
      }

      const storedTasks = storage.getItem('hintword_offline_tasks');
      if (storedTasks) {
        this.tasks = JSON.parse(storedTasks);
      }

      const storedFolders = storage.getItem('hintword_offline_folders');
      if (storedFolders) {
        this.folders = JSON.parse(storedFolders);
      }
    } catch (error) {
      console.error('Failed to load offline data from localStorage:', error);
    }
  }

  // Save data to localStorage
  saveToStorage(): void {
    try {
      storage.setItem('hintword_offline_notes', JSON.stringify(this.notes));
      storage.setItem('hintword_offline_collections', JSON.stringify(this.collections));
      storage.setItem('hintword_offline_tasks', JSON.stringify(this.tasks));
      storage.setItem('hintword_offline_folders', JSON.stringify(this.folders));
    } catch (error) {
      console.error('Failed to save offline data to localStorage:', error);
    }
  }

  // Notes management
  getNotes(): Note[] {
    return [...this.notes];
  }

  setNotes(notes: Note[]): void {
    this.notes = [...notes];
    this.saveToStorage();
  }

  addNote(note: Note): void {
    this.notes.unshift(note);
    this.saveToStorage();
  }

  updateNote(noteId: string, updates: Partial<Note>): void {
    const index = this.notes.findIndex(note => note.note_id === noteId);
    if (index !== -1) {
      this.notes[index] = { ...this.notes[index], ...updates };
      this.saveToStorage();
    }
  }

  deleteNote(noteId: string): void {
    this.notes = this.notes.filter(note => note.note_id !== noteId);
    this.saveToStorage();
  }

  // Collections management
  getCollections(): Collection[] {
    return [...this.collections];
  }

  setCollections(collections: Collection[]): void {
    this.collections = [...collections];
    this.saveToStorage();
  }

  addCollection(collection: Collection): void {
    this.collections.unshift(collection);
    this.saveToStorage();
  }

  updateCollection(collectionId: string, updates: Partial<Collection>): void {
    const index = this.collections.findIndex(collection => collection.collection_id === collectionId);
    if (index !== -1) {
      this.collections[index] = { ...this.collections[index], ...updates };
      this.saveToStorage();
    }
  }

  deleteCollection(collectionId: string): void {
    this.collections = this.collections.filter(collection => collection.collection_id !== collectionId);
    this.saveToStorage();
  }

  // Tasks management
  getTasks(): Task[] {
    return [...this.tasks];
  }

  setTasks(tasks: Task[]): void {
    this.tasks = [...tasks];
    this.saveToStorage();
  }

  addTask(task: Task): void {
    this.tasks.unshift(task);
    this.saveToStorage();
  }

  updateTask(taskId: string, updates: Partial<Task>): void {
    const index = this.tasks.findIndex(task => task.task_id === taskId);
    if (index !== -1) {
      this.tasks[index] = { ...this.tasks[index], ...updates };
      this.saveToStorage();
    }
  }

  deleteTask(taskId: string): void {
    this.tasks = this.tasks.filter(task => task.task_id !== taskId);
    this.saveToStorage();
  }

  // Task links (subtasks / related tasks)
  addTaskLink(taskId: string, link: TaskLink): void {
    const index = this.tasks.findIndex(task => task.task_id === taskId);
    if (index !== -1) {
      const existing = this.tasks[index].links || [];
      // Dedupe: same target + type is already linked.
      if (existing.some(l => l.linked_task_id === link.linked_task_id && l.link_type === link.link_type)) {
        return;
      }
      this.tasks[index] = { ...this.tasks[index], links: [...existing, link] };
      this.saveToStorage();
    }
  }

  removeTaskLink(taskId: string, taskLinkId: string): void {
    const index = this.tasks.findIndex(task => task.task_id === taskId);
    if (index !== -1) {
      const links = (this.tasks[index].links || []).filter(l => l.task_link_id !== taskLinkId);
      this.tasks[index] = { ...this.tasks[index], links };
      this.saveToStorage();
    }
  }

  remapTaskLinkId(oldId: string, newId: string): void {
    let changed = false;
    this.tasks = this.tasks.map(task => {
      if (!task.links?.some(l => l.task_link_id === oldId)) return task;
      changed = true;
      return {
        ...task,
        links: task.links.map(l => l.task_link_id === oldId ? { ...l, task_link_id: newId } : l),
      };
    });
    if (changed) this.saveToStorage();
  }

  // Folders (names/order synced on manual sync; grouping works offline)
  getFolders(): Folder[] {
    return [...this.folders];
  }

  setFolders(folders: Folder[]): void {
    this.folders = [...folders];
    this.saveToStorage();
  }

  upsertFolder(folder: Folder): void {
    const index = this.folders.findIndex(f => f.folder_id === folder.folder_id);
    if (index === -1) this.folders.push(folder);
    else this.folders[index] = folder;
    this.saveToStorage();
  }

  deleteFolder(slug: string): void {
    this.folders = this.folders.filter(f => f.slug !== slug);
    this.saveToStorage();
  }

  // Remap a locally-created id to the server-assigned id after sync
  remapNoteId(oldId: string, newId: string): void {
    const index = this.notes.findIndex(note => note.note_id === oldId);
    if (index !== -1) {
      this.notes[index] = { ...this.notes[index], note_id: newId };
      this.saveToStorage();
    }
  }

  remapCollectionId(oldId: string, newId: string): void {
    const index = this.collections.findIndex(collection => collection.collection_id === oldId);
    if (index !== -1) {
      this.collections[index] = { ...this.collections[index], collection_id: newId };
      this.saveToStorage();
    }
  }

  remapTabId(oldId: string, newId: string): void {
    let changed = false;
    this.collections = this.collections.map(collection => {
      if (!collection.tabs?.some(tab => tab.tab_id === oldId)) return collection;
      changed = true;
      return {
        ...collection,
        tabs: collection.tabs.map(tab =>
          tab.tab_id === oldId ? { ...tab, tab_id: newId } : tab
        ),
      };
    });
    if (changed) this.saveToStorage();
  }

  remapTaskId(oldId: string, newId: string): void {
    const index = this.tasks.findIndex(task => task.task_id === oldId);
    if (index !== -1) {
      this.tasks[index] = { ...this.tasks[index], task_id: newId };
      this.saveToStorage();
    }
  }

  // First-run seeding flag: has local storage ever been populated from the server?
  isSeeded(): boolean {
    try {
      return storage.getItem('hintword_offline_seeded') === '1';
    } catch {
      return false;
    }
  }

  markSeeded(): void {
    try {
      storage.setItem('hintword_offline_seeded', '1');
    } catch {}
  }

  // Clear all offline data
  clearAll(): void {
    this.notes = [];
    this.collections = [];
    this.tasks = [];
    this.folders = [];
    this.saveToStorage();
    try {
      storage.removeItem('hintword_offline_seeded');
    } catch {}
  }

  // Get offline data status
  getStatus(): { notesCount: number; collectionsCount: number; tasksCount: number } {
    return {
      notesCount: this.notes.length,
      collectionsCount: this.collections.length,
      tasksCount: this.tasks.length,
    };
  }
}

// Export singleton instance
export const offlineDataManager = OfflineDataManager.getInstance();

// Initialize from localStorage on module load
offlineDataManager.loadFromStorage();
